import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../components/CustomerLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ShoppingCart, Heart, Star, Shield, Truck, RotateCcw, Store, 
  Share2, ChevronDown, ChevronUp, Package, MapPin, Phone, Mail,
  CheckCircle, AlertCircle, Leaf, Sparkles, HeartPulse, Lightbulb
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import ShippingCalculator from '../components/ShippingCalculator';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import { formatPrice } from '../lib/utils';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [otherVendorProducts, setOtherVendorProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [showAbout, setShowAbout] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to generate slug from product name
  const generateSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^\w\s-]/g, '')
      .replace(/[-\s]+/g, '-')
      .trim()
      .replace(/^-+|-+$/g, '');
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  // Update URL to use slug when product loads (for SEO)
  useEffect(() => {
    if (product) {
      // Use existing slug or generate one from product name
      const slug = product.slug || generateSlug(product.name);
      
      // Replace URL with SEO-friendly slug URL if we're viewing by ID
      if (slug && id !== slug) {
        window.history.replaceState(null, '', `/products/${slug}`);
      }
      
      // Build keyword-rich title
      const vendorName = vendor?.business_name || '';
      const brandName = product.brand || vendorName;
      const pageTitle = brandName 
        ? `${product.name} | ${brandName} - Buy Online at NammaHut`
        : `${product.name} - Buy Online at Best Price | NammaHut`;
      document.title = pageTitle;
      
      // Build keyword-rich meta description
      const price = product.sale_price || product.price;
      const metaDesc = `Buy ${product.name} online at ₹${price}. ${product.description?.slice(0, 100) || ''} Free delivery across India. Shop authentic traditional Indian products at NammaHut.`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = metaDesc.slice(0, 160);
      
      // Update keywords meta
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        document.head.appendChild(metaKeywords);
      }
      const keywords = [
        product.name,
        product.name + ' online',
        product.name + ' price',
        'buy ' + product.name,
        brandName,
        brandName + ' ' + product.name,
        product.category || 'traditional indian sweets',
        'nammahut',
        'buy online india'
      ].filter(Boolean).join(', ');
      metaKeywords.content = keywords;
      
      // Update canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = `https://nammahut.com/products/${slug}`;
      
      // Update Open Graph tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.content = pageTitle;
      
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.content = metaDesc.slice(0, 160);
      
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.content = product.images?.[0] || 'https://nammahut.com/og-image.png';
      
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrl);
      }
      ogUrl.content = `https://nammahut.com/products/${slug}`;
      
      // Add og:type for product
      let ogType = document.querySelector('meta[property="og:type"]');
      if (!ogType) {
        ogType = document.createElement('meta');
        ogType.setAttribute('property', 'og:type');
        document.head.appendChild(ogType);
      }
      ogType.content = 'product';
      
      // Add product price meta
      let ogPrice = document.querySelector('meta[property="product:price:amount"]');
      if (!ogPrice) {
        ogPrice = document.createElement('meta');
        ogPrice.setAttribute('property', 'product:price:amount');
        document.head.appendChild(ogPrice);
      }
      ogPrice.content = price;
      
      let ogCurrency = document.querySelector('meta[property="product:price:currency"]');
      if (!ogCurrency) {
        ogCurrency = document.createElement('meta');
        ogCurrency.setAttribute('property', 'product:price:currency');
        document.head.appendChild(ogCurrency);
      }
      ogCurrency.content = 'INR';
      
      // Twitter Card tags
      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.name = 'twitter:title';
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.content = pageTitle;
      
      let twitterDesc = document.querySelector('meta[name="twitter:description"]');
      if (!twitterDesc) {
        twitterDesc = document.createElement('meta');
        twitterDesc.name = 'twitter:description';
        document.head.appendChild(twitterDesc);
      }
      twitterDesc.content = metaDesc.slice(0, 160);
      
      let twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (!twitterImage) {
        twitterImage = document.createElement('meta');
        twitterImage.name = 'twitter:image';
        document.head.appendChild(twitterImage);
      }
      twitterImage.content = product.images?.[0] || 'https://nammahut.com/og-image.png';
      
      // Add Product Schema (JSON-LD) for rich snippets
      let schemaScript = document.querySelector('script[data-schema="product"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.setAttribute('data-schema', 'product');
        document.head.appendChild(schemaScript);
      }
      
      const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description || '',
        "image": product.images || [],
        "url": `https://nammahut.com/products/${slug}`,
        "sku": product.sku || product.id || product._id,
        "brand": {
          "@type": "Brand",
          "name": product.brand || vendor?.business_name || "NammaHut"
        },
        "offers": {
          "@type": "Offer",
          "url": `https://nammahut.com/products/${slug}`,
          "priceCurrency": "INR",
          "price": product.sale_price || product.price,
          "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          "availability": product.inventory > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "seller": {
            "@type": "Organization",
            "name": vendor?.business_name || "NammaHut"
          }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "reviewCount": "24",
          "bestRating": "5",
          "worstRating": "1"
        },
        "category": product.category_id || "Traditional Indian Products"
      };
      
      schemaScript.textContent = JSON.stringify(productSchema);
      
      // Add BreadcrumbList Schema
      let breadcrumbScript = document.querySelector('script[data-schema="breadcrumb"]');
      if (!breadcrumbScript) {
        breadcrumbScript = document.createElement('script');
        breadcrumbScript.type = 'application/ld+json';
        breadcrumbScript.setAttribute('data-schema', 'breadcrumb');
        document.head.appendChild(breadcrumbScript);
      }
      
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://nammahut.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Products",
            "item": "https://nammahut.com/products"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": product.name,
            "item": `https://nammahut.com/products/${slug}`
          }
        ]
      };
      
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    }
  }, [product, id, vendor]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products/${id}`);
      setProduct(response.data);
      
      // Fetch vendor info if product has vendor_id
      if (response.data.vendor_id && response.data.vendor_id !== 'nammahut') {
        fetchVendorInfo(response.data.vendor_id);
      }
      
      // Fetch related products from same category
      if (response.data.category_id) {
        fetchRelatedProducts(response.data.category_id);
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorInfo = async (vendorId) => {
    try {
      const response = await axios.get(`${API_URL}/api/vendors/${vendorId}/public`);
      setVendor(response.data.vendor);
      setOtherVendorProducts(response.data.products?.filter(p => (p.id || p._id) !== id).slice(0, 5) || []);
    } catch (error) {
      console.error('Failed to fetch vendor info:', error);
    }
  };

  const fetchRelatedProducts = async (categoryId) => {
    try {
      const response = await axios.get(`${API_URL}/api/products`, {
        params: { limit: 6 }
      });
      const related = response.data
        .filter(p => (p.id || p._id) !== id)
        .slice(0, 5);
      setRelatedProducts(related);
    } catch (error) {
      console.error('Failed to fetch related products:', error);
    }
  };

  const handleAddToCart = () => {
    const productToAdd = {
      ...product,
      selectedVariant: hasVariants ? currentVariant : null,
      variantIndex: hasVariants ? selectedVariant : null
    };
    
    addToCart(productToAdd, quantity);
    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="min-h-screen">
          {/* SEO-friendly loading state - not a "not found" page */}
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="aspect-square bg-gray-200 rounded-lg"></div>
                <div className="space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-10 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            </div>
            {/* Hidden content for SEO crawlers */}
            <noscript>
              <div>
                <h1>Loading Product - NammaHut</h1>
                <p>Authentic traditional Indian sweets and snacks. Please enable JavaScript to view product details.</p>
                <p>Visit <a href="https://nammahut.com/products">NammaHut Products</a> to browse all products.</p>
              </div>
            </noscript>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  if (!product) {
    // Return actual 404 response hint via meta tag
    return (
      <CustomerLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            {/* Signal to Google this is a real 404 */}
            <meta name="robots" content="noindex" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-4">The product you're looking for doesn't exist or has been removed.</p>
            <Link to="/products" className="text-emerald-600 hover:underline">Browse All Products</Link>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  // Variant-aware calculations
  const hasVariants = product.variants && product.variants.length > 0;
  const currentVariant = hasVariants ? product.variants[selectedVariant] : null;
  const displayPrice = currentVariant 
    ? (currentVariant.sale_price || currentVariant.price) 
    : (product.sale_price || product.price);
  const originalPrice = currentVariant ? currentVariant.price : product.price;
  const currentInventory = currentVariant ? currentVariant.inventory : product.inventory;
  const currentSKU = currentVariant ? currentVariant.sku : product.sku;
  const hasDiscount = currentVariant 
    ? (currentVariant.sale_price && currentVariant.sale_price < currentVariant.price)
    : (product.sale_price && product.sale_price < product.price);
  const discountPercent = hasDiscount 
    ? Math.round((1 - displayPrice / originalPrice) * 100) 
    : 0;
  const isNammaHutProduct = !product.vendor_id || product.vendor_id === 'nammahut';

  // Product Details Table Data
  const productDetails = [
    { label: 'Country Of Origin', value: product.country_of_origin || 'India' },
    { label: 'Brand', value: product.brand || (isNammaHutProduct ? 'nammahut' : vendor?.business_name || 'Local Artisan') },
    { label: 'Container Name', value: product.container_name || 'Pack' },
    { label: 'Net Quantity', value: product.net_quantity || product.weight ? `${product.weight} kg` : '1 Unit' },
    { label: 'Manufacturer', value: product.manufacturer || (isNammaHutProduct ? 'nammahut' : vendor?.business_name || 'Traditional Maker') },
    { label: 'Manufacturer Address', value: product.manufacturer_address || (vendor?.address ? `${vendor.address.city}, ${vendor.address.state}` : 'India') },
    { label: 'Manufacturing Date', value: product.manufacturing_date || 'Fresh Stock' },
    { label: 'Contact Details', value: product.contact_details || 'support@nammahut.com' },
  ];

  // Add optional fields if present
  if (product.fssai_number) {
    productDetails.splice(1, 0, { label: 'FSSAI License', value: product.fssai_number, highlight: true });
  }
  if (product.ingredients) {
    productDetails.push({ label: 'Ingredients', value: product.ingredients });
  }
  if (product.shelf_life) {
    productDetails.push({ label: 'Shelf Life', value: product.shelf_life });
  }
  if (product.storage_instructions) {
    productDetails.push({ label: 'Storage', value: product.storage_instructions });
  }

  return (
    <CustomerLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {/* Breadcrumb */}
        <nav className="mb-4 text-sm flex items-center gap-2 text-gray-500">
          <Link to="/" className="hover:text-emerald-700">nammahut</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-emerald-700">Products</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium truncate max-w-xs">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Images */}
          <div className="lg:col-span-5">
            <div className="sticky top-20">
              {/* Main Image */}
              <div className="aspect-square mb-3 rounded-xl overflow-hidden bg-gray-100 relative border">
                <img
                  src={product.images?.[selectedImage] || 'https://via.placeholder.com/600x600?text=nammahut'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {hasDiscount && (
                    <Badge className="bg-red-500 text-white font-bold px-3 py-1">
                      {discountPercent}% OFF
                    </Badge>
                  )}
                  {isNammaHutProduct && (
                    <Badge className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
                      <Store className="w-3 h-3 mr-1" /> nammahut
                    </Badge>
                  )}
                </div>
                {/* Quick Actions */}
                <div className="absolute top-3 right-3 flex flex-col gap-2">
                  <button className="w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50">
                    <Heart className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50">
                    <Share2 className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
              
              {/* Thumbnail Images */}
              {product.images?.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {product.images.map((image, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === idx ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img src={image} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Middle Column - Product Info */}
          <div className="lg:col-span-4">
            {/* Vendor Link */}
            {vendor && (
              <Link 
                to={`/vendor/${vendor.id}/store`}
                className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 mb-2"
              >
                <Store className="w-4 h-4" />
                <span className="font-medium">{vendor.business_name}</span>
              </Link>
            )}

            <h1 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">{product.name}</h1>
            
            {/* Rating */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center bg-emerald-50 px-2 py-1 rounded">
                <span className="font-bold text-emerald-700 mr-1">4.5</span>
                <Star className="w-4 h-4 text-emerald-600 fill-emerald-600" />
              </div>
              <span className="text-sm text-gray-500">24 Ratings & 8 Reviews</span>
              {product.fssai_number && (
                <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
                  <CheckCircle className="w-3 h-3 mr-1" /> FSSAI
                </Badge>
              )}
            </div>

            {/* Price */}
            <div className="mb-5 pb-5 border-b">
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-3xl font-bold text-gray-900">₹{displayPrice.toLocaleString()}</span>
                {hasDiscount && (
                  <>
                    <span className="text-xl text-gray-400 line-through">₹{originalPrice.toLocaleString()}</span>
                    <span className="text-emerald-600 font-semibold">({discountPercent}% OFF)</span>
                  </>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">Inclusive of all taxes</p>
            </div>

            {/* Key Highlights */}
            <div className="mb-5">
              <h3 className="font-semibold text-gray-900 mb-3">Key Highlights</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="w-4 h-4 text-emerald-600" />
                  <span>Free Shipping</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <RotateCcw className="w-4 h-4 text-emerald-600" />
                  <span>Easy Returns</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="w-4 h-4 text-emerald-600" />
                  <span>100% Authentic</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Leaf className="w-4 h-4 text-emerald-600" />
                  <span>Traditional Recipe</span>
                </div>
              </div>
            </div>

            {/* Variant Selector */}
            {hasVariants && (
              <div className="mb-5">
                <label className="block font-semibold text-gray-900 mb-3">Select Weight/Size</label>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((variant, index) => (
                    <button
                      key={index}
                      className={`px-4 py-2.5 border-2 rounded-lg font-medium transition-all ${
                        selectedVariant === index 
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      } ${variant.inventory === 0 ? 'opacity-50 cursor-not-allowed line-through' : ''}`}
                      onClick={() => variant.inventory > 0 && setSelectedVariant(index)}
                      disabled={variant.inventory === 0}
                      data-testid={`variant-btn-${index}`}
                    >
                      <span className="block text-sm">{variant.name || `${variant.weight}g`}</span>
                      <span className="block text-xs mt-0.5">
                        {variant.sale_price ? (
                          <>
                            <span className="text-emerald-600 font-semibold">₹{variant.sale_price}</span>
                            <span className="ml-1 line-through text-gray-400">₹{variant.price}</span>
                          </>
                        ) : (
                          <span className="text-emerald-600 font-semibold">₹{variant.price}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-5">
              <label className="block font-semibold text-gray-900 mb-3">Quantity</label>
              <div className="flex items-center gap-3">
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 text-lg font-medium"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(currentInventory, quantity + 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 text-lg font-medium"
                  >
                    +
                  </button>
                </div>
                <span className={`text-sm ${currentInventory > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {currentInventory > 0 ? `${currentInventory} in stock` : 'Out of Stock'}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-5">
              <Button
                onClick={handleAddToCart}
                disabled={currentInventory === 0}
                className="flex-1 py-6 text-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
              <Button 
                onClick={() => {
                  handleAddToCart();
                  setTimeout(() => navigate('/cart'), 500);
                }}
                disabled={currentInventory === 0}
                variant="outline"
                className="flex-1 py-6 text-lg border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              >
                Buy Now
              </Button>
            </div>

            {/* Delivery Check */}
            <div className="bg-gray-50 rounded-xl p-4">
              <ShippingCalculator productId={id} vendorId={product.vendor_id} compact />
            </div>
          </div>

          {/* Right Column - Seller Info & Details */}
          <div className="lg:col-span-3">
            {/* Seller Card */}
            {vendor && (
              <Card className="mb-4 overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center overflow-hidden">
                      {vendor.logo ? (
                        <img src={vendor.logo} alt={vendor.business_name} className="w-full h-full object-cover" />
                      ) : (
                        <Store className="w-7 h-7 text-emerald-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{vendor.business_name}</h3>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <MapPin className="w-3 h-3" />
                        <span>{vendor.address?.city || 'India'}</span>
                      </div>
                    </div>
                  </div>
                  <Link 
                    to={`/vendor/${vendor.id}/store`}
                    className="block w-full text-center py-2 border-2 border-emerald-500 text-emerald-700 rounded-lg font-medium hover:bg-emerald-50 transition-colors"
                  >
                    Visit Store
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Product Details Accordion */}
            <Card className="mb-4">
              <button 
                onClick={() => setShowDetails(!showDetails)}
                className="w-full p-4 flex items-center justify-between text-left"
              >
                <h3 className="font-bold text-gray-900">Product Details</h3>
                {showDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {showDetails && (
                <CardContent className="pt-0 px-4 pb-4">
                  <div className="divide-y divide-gray-100">
                    {productDetails.map((item, idx) => (
                      <div key={idx} className="py-2.5 flex gap-3">
                        <span className="w-28 text-sm text-gray-500 flex-shrink-0">{item.label}</span>
                        <span className={`text-sm flex-1 break-all ${item.highlight ? 'text-emerald-700 font-medium' : 'text-gray-900'}`}>
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* About Product Accordion */}
            <Card>
              <button 
                onClick={() => setShowAbout(!showAbout)}
                className="w-full p-4 flex items-center justify-between text-left"
              >
                <h3 className="font-bold text-gray-900">About Product</h3>
                {showAbout ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {showAbout && (
                <CardContent className="pt-0 px-4 pb-4">
                  <p className="text-sm text-gray-700 leading-relaxed">{product.description}</p>
                </CardContent>
              )}
            </Card>
          </div>
        </div>

        {/* Fun Facts & Health Benefits Section - Prominent Display */}
        {(product.history_fact || product.health_benefits) && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Did You Know? - Fun Facts */}
            {product.history_fact && (
              <Card className="overflow-hidden border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-amber-800 text-lg mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Did you know?
                      </h3>
                      <p className="text-amber-900 leading-relaxed text-sm italic">
                        {product.history_fact}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Health Benefits */}
            {product.health_benefits && (
              <Card className="overflow-hidden border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <HeartPulse className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-green-800 text-lg mb-2 flex items-center gap-2">
                        <Leaf className="w-4 h-4" />
                        Health Benefits
                      </h3>
                      <p className="text-green-900 leading-relaxed text-sm">
                        {product.health_benefits}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Other Products from Same Seller */}
        {otherVendorProducts.length > 0 && vendor && (
          <section className="mt-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-900">Other Products from {vendor.business_name}</h2>
              <Link to={`/vendor/${vendor.id}/store`} className="text-emerald-600 hover:text-emerald-700 font-medium text-sm">
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {otherVendorProducts.map((item) => (
                <ProductCard key={item.id || item._id} product={item} />
              ))}
            </div>
          </section>
        )}

        {/* Similar Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xl font-bold text-gray-900 mb-5">Similar Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {relatedProducts.map((item) => (
                <ProductCard key={item.id || item._id} product={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </CustomerLayout>
  );
}

// Product Card Component
function ProductCard({ product }) {
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.sale_price / product.price) * 100) : 0;
  
  // Use slug for SEO-friendly URLs
  const productUrl = product.slug || product.id || product._id;

  return (
    <Link to={`/products/${productUrl}`} className="group">
      <Card className="overflow-hidden h-full border hover:shadow-lg transition-all">
        <div className="relative aspect-square bg-gray-100">
          <img 
            src={product.images?.[0] || 'https://via.placeholder.com/200?text=nammahut'} 
            alt={product.name} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
          />
          {hasDiscount && (
            <Badge className="absolute top-2 left-2 bg-red-500 text-white text-xs">
              {discountPercent}% OFF
            </Badge>
          )}
        </div>
        <CardContent className="p-3">
          <h3 className="font-medium text-gray-800 text-sm line-clamp-2 mb-2 group-hover:text-emerald-700">
            {product.name}
          </h3>
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-900">₹{(product.sale_price || product.price).toLocaleString()}</span>
            {hasDiscount && (
              <span className="text-xs text-gray-400 line-through">₹{product.price.toLocaleString()}</span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
